import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { theme } from '../theme'
import { EfaxWordmark } from './EfaxWordmark'
import { UserClock } from './UserClock'
import { PressureBanner } from './PressureBanner'
import { useIsAdmin } from '../auth/AuthContext'
import { tacticalChildren } from '../tacticalNav'
import {
  PROVIDER_ACCOUNTS_PATH,
  buildSettingsMenu,
  type SettingsMenuItem,
} from '../settingsNav'

const navItems = [
  { path: '/', label: 'Dashboard' },
  { path: '/jobs', label: 'Jobs' },
  { path: '/tactical', label: 'Tactical E2E' },
  { path: '/reports', label: 'Reports' },
]

// Help is rendered SEPARATELY, after the Settings menu — see the JSX below.
//
// It cannot live in navItems and still sit after Settings, because Settings is not a
// nav item at all: it is an admin-gated dropdown emitted after navItems.map().
// So ordering Help last is structural, not an array reorder.
//
// It stays OUTSIDE the isAdmin gate. A viewer needs to know what 'degraded' or 'p90'
// means at least as much as an admin does, and the content describes behaviour rather
// than exposing configuration or data.
const HELP_ITEM = { path: '/help', label: 'Help' }

// Grouped under a single Settings menu. Every entry is admin-only server-side
// (metrics credentials name the accounts guarding /metrics; identity claims
// expose everyone's group membership), so the whole menu is hidden from viewers
// rather than offering links that only return 403.
//
// THIS FILE DECLARES NO SETTINGS PATH OF ITS OWN. The menu used to be a hand-written
// array here, kept in step with App.tsx's routes by memory, and it drifted — quality and
// audit were routed, worked, and were in nobody's menu. Both lists now come from
// `../settingsNav`, and tests/test_settings_menu_covers_every_route.py fails if a
// `/settings/<page>` literal reappears in this file.

// Grace period before a pointer leaving the menu closes it, so crossing the small gap
// between the trigger and the panel — or clipping a corner on the way to an item — does
// not dismiss what you were aiming at.
const HOVER_CLOSE_MS = 180

export default function Layout() {
  const location = useLocation()
  const isAdmin = useIsAdmin()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const settingsRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<number | null>(null)

  // The account kinds, for the Provider Accounts submenu. Admin-only, because the
  // endpoint is admin-gated and a viewer never renders this menu.
  const [accountKinds, setAccountKinds] = useState<{ kind: string; label: string }[]>([])

  useEffect(() => {
    if (!isAdmin) return
    let cancelled = false
    fetch('/api/settings/provider-accounts/kinds', { credentials: 'same-origin' })
      .then(res => (res.ok ? res.json() : null))
      .then(body => {
        if (cancelled || !body) return
        const kinds = (body as { kinds?: { kind: string; label: string }[] }).kinds ?? []
        setAccountKinds(kinds.map(k => ({ kind: k.kind, label: k.label })))
      })
      // A failed fetch leaves the parent as a plain link rather than hiding it: the page
      // itself lists the kinds, so the surface stays reachable. A submenu that could not
      // load must not take the surface down with it.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [isAdmin])

  // Derived, never written: `buildSettingsMenu` folds the declaration in ../settingsNav,
  // and the only thing added here is the children that cannot be declared statically —
  // the provider account kinds, which come from the provider registry at runtime.
  const settingsItems: SettingsMenuItem[] = buildSettingsMenu(
    accountKinds.length > 0
      ? {
          [PROVIDER_ACCOUNTS_PATH]: accountKinds.map(k => ({
            path: `${PROVIDER_ACCOUNTS_PATH}/${k.kind}`,
            label: k.label,
          })),
        }
      : {},
  )

  const inSettings = location.pathname.startsWith('/settings')

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const openSettings = useCallback(() => {
    cancelClose()
    setSettingsOpen(true)
  }, [cancelClose])

  const closeSettings = useCallback(
    (returnFocus = false) => {
      cancelClose()
      setSettingsOpen(false)
      if (returnFocus) triggerRef.current?.focus()
    },
    [cancelClose],
  )

  // HOVER IS AN ACCELERATOR, NOT THE MECHANISM. Everything below works from the keyboard
  // first — the trigger is a real <button>, so Enter and Space toggle it natively — and
  // pointer hover only reaches the same open/close calls. A CSS :hover submenu would be
  // faster to write and unreachable for anyone not using a mouse.
  const hoverOpen = () => openSettings()
  const hoverClose = () => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => setSettingsOpen(false), HOVER_CLOSE_MS)
  }

  useEffect(() => cancelClose, [cancelClose])

  /** The focusable items of the open panel, in visual order (parents and children alike). */
  const menuLinks = (): HTMLAnchorElement[] =>
    Array.from(panelRef.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]') ?? [])

  const focusItem = (index: number) => {
    const links = menuLinks()
    if (links.length === 0) return
    // Wrap, so ArrowDown off the end lands back at the top rather than dead-ending.
    const target = ((index % links.length) + links.length) % links.length
    links[target].focus()
  }

  const moveFocus = (delta: number) => {
    const links = menuLinks()
    if (links.length === 0) return
    const current = links.indexOf(document.activeElement as HTMLAnchorElement)
    focusItem(current === -1 ? (delta > 0 ? 0 : links.length - 1) : current + delta)
  }

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    // ArrowDown/ArrowUp open the menu AND move into it, which is what a screen-reader
    // user expects of aria-haspopup. Enter/Space are the button's own and need no code.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      openSettings()
      // The panel mounts on the next render, so defer the focus to after it exists.
      window.setTimeout(() => focusItem(e.key === 'ArrowDown' ? 0 : -1), 0)
    }
  }

  const onPanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        moveFocus(1)
        break
      case 'ArrowUp':
        e.preventDefault()
        moveFocus(-1)
        break
      case 'Home':
        e.preventDefault()
        focusItem(0)
        break
      case 'End':
        e.preventDefault()
        focusItem(-1)
        break
      case 'Tab':
        // Let Tab do its normal thing, but do not leave an orphaned panel behind it.
        closeSettings()
        break
      default:
        break
    }
  }

  // Close on outside click or Escape so the menu never strands over the page. Escape
  // returns focus to the trigger — otherwise a keyboard user is left with focus on a
  // node that no longer exists and has to Tab in from the top of the page.
  useEffect(() => {
    if (!settingsOpen) return
    const onDown = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        closeSettings()
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSettings(true)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [settingsOpen, closeSettings])

  // Navigating away should dismiss it — otherwise it stays open over the new page.
  useEffect(() => { setSettingsOpen(false) }, [location.pathname])

  const linkStyle = (active: boolean): React.CSSProperties => ({
    color: theme.onPrimary,
    opacity: active ? 1 : 0.8,
    textDecoration: 'none',
    fontSize: '0.9rem',
    padding: '0.25rem 0.5rem',
    fontWeight: active ? 600 : 400,
    borderBottom: active ? `2px solid ${theme.onPrimary}` : '2px solid transparent',
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', fontFamily: theme.fontFamily, color: theme.text }}>
      <header style={{ background: theme.headerBg, color: theme.onPrimary, padding: '0 2rem', display: 'flex', alignItems: 'center', height: '56px', gap: '2rem' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
          <EfaxWordmark height={26} />
          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: theme.onPrimary, opacity: 0.9 }}>E2E Scheduler</span>
        </Link>

        <nav style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {navItems.map(item => (
            <Fragment key={item.path}>
              <Link to={item.path} style={linkStyle(location.pathname === item.path)}>
                {item.label}
              </Link>
              {/* ONE NESTED LEVEL, DERIVED — never written here. Tactical E2E had no
                  sub-page precedent: this nav is flat and Settings was the only entry
                  that nested. The rows live in ../tacticalNav for the reason the
                  settings menu is derived from ../settingsNav: a hand-kept list drifted
                  from App.tsx's routes and left two finished pages reachable only by
                  typing a URL.

                  `tacticalChildren` is called for EVERY item and returns [] for the ones
                  with none, so no call site here has to know which parents nest — that
                  knowledge is a second place for the two lists to disagree.

                  RENDERED UNCONDITIONALLY, not only while the parent is active: a child
                  that appears after you have already navigated to its parent is
                  discoverable exactly by the people who already knew it was there. */}
              {tacticalChildren(item.path).map(child => (
                <Link
                  key={child.path}
                  to={child.path}
                  style={{
                    ...linkStyle(location.pathname === child.path),
                    fontSize: '0.8rem',
                    paddingLeft: '0.15rem',
                    opacity: location.pathname === child.path ? 1 : 0.65,
                  }}
                >
                  <span aria-hidden="true" style={{ opacity: 0.6, marginRight: '0.3rem' }}>
                    ›
                  </span>
                  {child.label}
                </Link>
              ))}
            </Fragment>
          ))}

          {isAdmin && (
            <div
              ref={settingsRef}
              style={{ position: 'relative' }}
              onMouseEnter={hoverOpen}
              onMouseLeave={hoverClose}
              // Focus moving anywhere inside keeps it open; focus leaving closes it. This is
              // what makes Shift+Tab out of the panel behave like clicking away.
              onFocus={openSettings}
              onBlur={e => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) closeSettings()
              }}
            >
              <button
                ref={triggerRef}
                onClick={() => (settingsOpen ? closeSettings() : openSettings())}
                onKeyDown={onTriggerKeyDown}
                aria-haspopup="menu"
                aria-expanded={settingsOpen}
                style={{
                  ...linkStyle(inSettings),
                  background: 'transparent',
                  border: 'none',
                  borderBottom: inSettings ? `2px solid ${theme.onPrimary}` : '2px solid transparent',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                }}
              >
                Settings
                <span aria-hidden="true" style={{ fontSize: '0.6rem', lineHeight: 1 }}>{settingsOpen ? '▲' : '▼'}</span>
              </button>

              {settingsOpen && (
                // The visual gap is PADDING on this positioned wrapper, not a margin on the
                // panel: a margin would leave a dead strip between the trigger and the first
                // item that a pointer crosses, closing the menu on the way to it.
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    paddingTop: '0.6rem',
                    zIndex: 50,
                  }}
                >
                  <div
                    ref={panelRef}
                    role="menu"
                    aria-label="Settings"
                    onKeyDown={onPanelKeyDown}
                    style={{
                      minWidth: 190,
                      background: theme.bgElevated,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 6,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.6)',
                      padding: '0.3rem',
                    }}
                  >
                  {settingsItems.map(item => {
                    // A PARENT MATCHES BY PREFIX, a leaf by equality. `===` on a parent leaves
                    // Settings → Provider Accounts unhighlighted the moment you open one of its
                    // kinds, which reads as "you are somewhere else".
                    const active = item.children
                      ? location.pathname.startsWith(item.path)
                      : location.pathname === item.path
                    return (
                      <div key={item.path}>
                        <Link
                          to={item.path}
                          role="menuitem"
                          style={{
                            display: 'block',
                            padding: '0.45rem 0.6rem',
                            fontSize: '0.85rem',
                            textDecoration: 'none',
                            borderRadius: 4,
                            color: active ? theme.primary : theme.text,
                            fontWeight: active ? 600 : 400,
                            background: active ? theme.bgAlt : 'transparent',
                          }}
                        >
                          {item.label}
                        </Link>
                        {/* ONE nested level. A child is active on exact match only — it is a
                            leaf, and there is nothing below it to be inside of. */}
                        {item.children?.map(child => {
                          const childActive = location.pathname === child.path
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              role="menuitem"
                              style={{
                                display: 'block',
                                padding: '0.35rem 0.6rem 0.35rem 1.4rem',
                                fontSize: '0.8rem',
                                textDecoration: 'none',
                                borderRadius: 4,
                                color: childActive ? theme.primary : theme.textMuted,
                                fontWeight: childActive ? 600 : 400,
                                background: childActive ? theme.bgAlt : 'transparent',
                              }}
                            >
                              {child.label}
                            </Link>
                          )
                        })}
                      </div>
                    )
                  })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Help LAST, after Settings, and deliberately outside the isAdmin gate
              above — inside it, Help would disappear entirely for viewers, who are
              exactly the people most likely to need the glossary. */}
          <Link to={HELP_ITEM.path} style={linkStyle(location.pathname === HELP_ITEM.path)}>
            {HELP_ITEM.label}
          </Link>
        </nav>

        {/* Lives in Layout, so identity + clock appear on every page. */}
        <UserClock />
      </header>
      {/* Between </header> and <main> is the only place a full-width bar appears on every
          page without touching each page component. It renders NOTHING below threshold --
          no placeholder and no reserved space -- so its appearance must not shift the
          header. Deliberately OUTSIDE the isAdmin gate: a viewer watching a slow dashboard
          benefits most from knowing the delay is ours and not the fax service's. */}
      <PressureBanner />
      <main style={{ flex: 1, padding: '1.5rem 2rem', background: theme.bgAlt }}>
        <Outlet />
      </main>
    </div>
  )
}
